/**
 * AppExitModal — attractive exit confirmation modal.
 *
 * Shown when the user clicks the window close icon across desktop pages.
 * Uses the same visual language as the logout confirmation modal.
 */
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { HiOutlineLogout } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";

import { sendAppExitConfirmation } from "@service/ipc-handler";
import { logout } from "@service/user-handler";
import { exitAppReducer } from "@store/ipc-events-modal-splice";
import { getColorTheme } from "@service/local-storage-handler";

import "./app-exit-modal.scss";

const AppExitModal: React.FC = () => {
  const dispatch = useDispatch();
  const { exitApp } = useSelector(({ ipcEventsModal }) => ipcEventsModal);
  const { isAuthenticated } = useSelector(({ auth }) => auth);

  const themeFromStore = useSelector(({ theme }) => theme.theme);
  const theme = (themeFromStore || getColorTheme() || "dark") as "light" | "dark";

  useEffect(() => {
    if (exitApp) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [exitApp]);

  const closeModal = () => {
    sendAppExitConfirmation(false);
    dispatch(exitAppReducer({ exitApp: false }));
  };

  const handleYesButton = async () => {
    try {
      if (isAuthenticated) {
        await logout();
      }
    } catch (error) {
      console.error(error);
    } finally {
      await sendAppExitConfirmation(true);
      dispatch(exitAppReducer({ exitApp: false }));
    }
  };

  if (!exitApp) return null;

  return createPortal(
    <div
      className={`axm-overlay ${theme}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) closeModal();
      }}
    >
      <div className={`axm-card ${theme}`}>
        <div className="axm-header">
          <div className="axm-header__bg" />
          <div className="axm-icon-wrap">
            <div className="axm-icon">
              <HiOutlineLogout size={26} />
            </div>
          </div>
        </div>

        <div className="axm-body">
          <h3 className="axm-title">Confirm Exit</h3>
          <div className="axm-divider" />
          <p className="axm-desc">Are you sure you want to exit the app?</p>
        </div>

        <div className="axm-footer">
          <button className="axm-btn axm-btn--cancel" onClick={closeModal}>
            No, Cancel
          </button>
          <button className="axm-btn axm-btn--confirm" onClick={handleYesButton}>
            Yes, Exit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AppExitModal;
